// PlantCard — detailed plant tile for the Atlas left rail and the field-schematic
// panel. Leads with the client logo (or initials monogram) + a plain-language
// health verdict, then ASSETS / CMLs / CMLs<min. Click opens the plant site map.
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MapPin, ArrowUpRight, Anchor, AlertTriangle, CheckCircle2, MinusCircle } from "lucide-react";
import type { Plant } from "@/lib/types";
import { CLIENT_LOGOS } from "@/lib/clientLogos";

export interface PlantCardProps {
  plant: Plant;
  isActive: boolean;
  animDelay?: number;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  cardRef?: (el: HTMLDivElement | null) => void;
}

// 3-tier health, shared with the Atlas rail: red below t_min, amber a critical
// finding without a breach, green healthy, grey no data.
type HealthTier = "crit" | "watch" | "ok" | "none";
function healthTier(p: Plant): HealthTier {
  if ((p.cmls_below_min || 0) > 0) return "crit";
  if ((p.crit_findings || 0) > 0) return "watch";
  if ((p.cml_count || 0) > 0) return "ok";
  return "none";
}
// Left LITERAL on purpose. This is the same four-band health vocabulary
// AtlasScreen carries (crit / watch / ok / none), only two of whose members
// have a theme role — converting `crit` alone would have made the identical
// "Action needed" chip a different red here than on the map. The values also
// feed `color + "59"` / `color + "14"` below, which is not a colour once the
// base is a var().
const TIER_META: Record<HealthTier, { color: string; label: string }> = {
  crit:  { color: "#C0392B", label: "Action needed" },
  watch: { color: "#E0A500", label: "Watch" },
  ok:    { color: "#1FA85C", label: "Healthy" },
  none:  { color: "#6E7585", label: "No data" },
};

function HealthChip({ plant }: { plant: Plant }): JSX.Element {
  const t = healthTier(plant);
  const { color, label } = TIER_META[t];
  const Icon = t === "ok" ? CheckCircle2 : t === "none" ? MinusCircle : AlertTriangle;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 border font-mono text-[9px] uppercase tracking-wide whitespace-nowrap"
      style={{ color, borderColor: color + "59", background: color + "14", borderRadius: 5 }}
    >
      <Icon size={9} className="shrink-0" /> {label}
    </span>
  );
}

function initials(p: Plant): string {
  const src = (p.client_name || p.name || p.code || "?").replace(/\(.*?\)/g, " ").trim();
  const w = src.split(/\s+/).filter(Boolean);
  const raw = w.length >= 2 ? w[0][0] + w[1][0] : src.slice(0, 2);
  return raw.toUpperCase().slice(0, 2);
}

function ClientLogo({ plant }: { plant: Plant }): JSX.Element {
  const src = plant.client_id != null ? CLIENT_LOGOS[plant.client_id] : undefined;
  if (src) {
    return <img src={src} alt="" className="shrink-0 object-contain" style={{ width: 34, height: 34 }} />;
  }
  return (
    <div className="shrink-0 flex items-center justify-center font-display font-bold text-brand select-none"
         style={{ width: 34, height: 34, border: "1.5px solid currentColor", background: "rgba(var(--accent-rgb) / 0.08)", fontSize: 12.5 }}>
      {initials(plant)}
    </div>
  );
}

export default function PlantCard({
  plant, isActive, animDelay = 0, onMouseEnter, onMouseLeave, cardRef,
}: PlantCardProps): JSX.Element {
  const navigate = useNavigate();
  const tier = healthTier(plant);
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: animDelay }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="shrink-0"
      ref={cardRef}
    >
      <button
        onClick={() => navigate("/sites/" + plant.id)}
        className={"block w-full text-left relative overflow-hidden panel-raised transition-all group tier-" + tier + " " + (isActive ? "border-brand shadow-glow" : "hover:border-brand/60")}
      >
        <div className="px-3 py-2 flex items-start justify-between gap-2">
          <div className="flex items-start gap-2.5 min-w-0 flex-1">
            <ClientLogo plant={plant} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[11px] uppercase tracking-wider text-brand">{plant.code}</span>
                {plant.installation_year && (
                  <span className="font-mono text-[9px] text-ink-3">/ {plant.installation_year}</span>
                )}
              </div>
              <h2 className="font-display font-bold text-base text-ink-1 mt-0.5 leading-tight truncate">{plant.name}</h2>
              <div className="text-[10px] text-ink-3 mt-0.5 truncate">{plant.location || plant.client_name}</div>
              {plant.lat != null && plant.lon != null && (
                <div className="mt-1 flex items-center gap-1.5 font-mono text-[9px] text-ink-3">
                  <MapPin size={8} className="text-brand/70 shrink-0" />
                  <span className="truncate">{Number(plant.lat).toFixed(3)}N {Number(plant.lon).toFixed(3)}E</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0 mt-0.5">
            <HealthChip plant={plant} />
            <ArrowUpRight size={14}
              className="text-ink-3 group-hover:text-brand group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-px bg-panel-700 border-y border-panel-700">
          <Stat label="ASSETS" value={plant.asset_count} />
          <Stat label="CMLs" value={plant.cml_count} />
          <Stat label="CMLs<min" value={plant.cmls_below_min || 0} crit />
        </div>

        <div className="px-3 py-1.5 bg-panel-950 flex items-center justify-between font-mono text-[9px] uppercase tracking-wider">
          <span className="text-ink-3 flex items-center gap-1.5 truncate">
            <Anchor size={8} className="text-status-ok shrink-0" />
            {plant.client_name}
          </span>
          <span className="text-brand whitespace-nowrap">OPEN</span>
        </div>
      </button>
    </motion.div>
  );
}

function Stat({ label, value, crit }: { label: string; value: number; crit?: boolean }): JSX.Element {
  return (
    <div className="bg-panel-850 px-3 py-1">
      <div className="label-eng text-[9px] flex items-center gap-1">
        {label}
        {crit && value > 0 && <AlertTriangle size={8} className="text-status-crit" />}
      </div>
      <div className={"font-mono text-sm tabular leading-tight " + (crit && value > 0 ? "text-status-crit font-bold" : "text-ink-1")}>
        {value}
      </div>
    </div>
  );
}
